'use client';

import {
  isAacFile,
  isAudioFile,
  isCuttableAudioFile,
  isM4aFile,
  isMp3File,
  isVideoFile,
  isVoiceNoteFile,
  isWavFile,
  readMediaFileMetadata,
  type MediaFileKind,
  type MediaFileMetadata,
} from '@/lib/browser-audio-tools';
import { useEffect, useState } from 'react';

export type AudioSourceMode =
  | 'mp3'
  | 'audio'
  | 'video'
  | 'audio-cutter'
  | 'm4a'
  | 'wav'
  | 'aac'
  | 'voice-note';

export type LoadedMediaSource = {
  file: File;
  previewUrl: string;
  metadata: MediaFileMetadata;
  kind: MediaFileKind;
};

export type AudioRecorderFormat = {
  mimeType: string;
  extension: string;
  label: string;
};

const recorderFormats: AudioRecorderFormat[] = [
  { mimeType: 'audio/webm;codecs=opus', extension: 'webm', label: 'WebM' },
  { mimeType: 'audio/webm', extension: 'webm', label: 'WebM' },
  { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg', label: 'Ogg' },
  { mimeType: 'audio/mp4', extension: 'm4a', label: 'M4A' },
];

export function formatDurationMs(durationMs: number) {
  const totalSeconds = Math.max(0, Math.round(durationMs / 10) / 100);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`;
  }

  return `${seconds.toFixed(2)}s`;
}

export function formatBitrateKbps(value: number | null) {
  return value ? `${value} kbps` : 'Unknown';
}

function getModeCopy(mode: AudioSourceMode) {
  if (mode === 'mp3') {
    return {
      validator: isMp3File,
      message: 'Choose one MP3 file for this tool.',
      kind: 'audio' as const,
    };
  }

  if (mode === 'video') {
    return {
      validator: isVideoFile,
      message: 'Choose one video file for this tool.',
      kind: 'video' as const,
    };
  }

  if (mode === 'audio-cutter') {
    return {
      validator: isCuttableAudioFile,
      message: 'Choose one M4A, MP3, WAV, AAC, or OGG audio file for this tool.',
      kind: 'audio' as const,
    };
  }

  if (mode === 'm4a') {
    return {
      validator: isM4aFile,
      message: 'Choose one M4A audio file for this tool.',
      kind: 'audio' as const,
    };
  }

  if (mode === 'wav') {
    return {
      validator: isWavFile,
      message: 'Choose one WAV audio file for this tool.',
      kind: 'audio' as const,
    };
  }

  if (mode === 'aac') {
    return {
      validator: isAacFile,
      message: 'Choose one AAC audio file for this tool.',
      kind: 'audio' as const,
    };
  }

  if (mode === 'voice-note') {
    return {
      validator: isVoiceNoteFile,
      message: 'Choose one voice note, recording, or audio file for this tool.',
      kind: 'audio' as const,
    };
  }

  return {
    validator: isAudioFile,
    message: 'Choose one audio file for this tool.',
    kind: 'audio' as const,
  };
}

export function useLoadedMediaSource(mode: AudioSourceMode) {
  const [source, setSource] = useState<LoadedMediaSource | null>(null);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  async function loadSource(file: File) {
    const copy = getModeCopy(mode);

    if (!copy.validator(file)) {
      throw new Error(copy.message);
    }

    const metadata = await readMediaFileMetadata(file, copy.kind);

    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return {
        file,
        metadata,
        kind: copy.kind,
        previewUrl: URL.createObjectURL(file),
      };
    });
  }

  function clearSource() {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return null;
    });
  }

  return {
    source,
    loadSource,
    clearSource,
  };
}

export function canRecordAudioInBrowser() {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function getSupportedAudioRecorderFormat() {
  if (!canRecordAudioInBrowser()) {
    return null;
  }

  return (
    recorderFormats.find((candidate) => MediaRecorder.isTypeSupported(candidate.mimeType)) ?? null
  );
}

export const acceptedAudioMimeTypes =
  'audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/aac,audio/ogg,audio/flac,audio/webm,audio/*';

export const acceptedCuttableAudioMimeTypes =
  'audio/mpeg,.mp3,audio/wav,.wav,audio/x-wav,audio/mp4,.m4a,audio/aac,.aac,audio/ogg,.ogg,.oga';

export const acceptedM4aMimeTypes = 'audio/mp4,audio/x-m4a,.m4a,.mp4,.m4b';

export const acceptedWavMimeTypes = 'audio/wav,audio/x-wav,.wav,.wave';

export const acceptedAacMimeTypes = 'audio/aac,.aac';

// Kept in step with voiceNoteExtensionPattern in browser-audio-tools.ts. The picker is the first
// gate a WhatsApp .opus or a recorder's .amr hits, so a format missing here is rejected before any
// of our code runs, however well ffmpeg would have handled it.
export const acceptedVoiceNoteMimeTypes =
  'audio/mp4,.m4a,audio/aac,.aac,audio/ogg,.ogg,.oga,audio/opus,.opus,audio/amr,.amr,audio/3gpp,.3gp,.3gpp,audio/x-caf,.caf,audio/x-ms-wma,.wma,audio/webm,.webm,audio/mpeg,.mp3,audio/wav,.wav,audio/x-wav';

export const acceptedVideoMimeTypes =
  'video/mp4,video/webm,video/quicktime,video/x-m4v,video/mpeg,video/avi,video/*';
